import React, { createContext, useContext } from 'react'
import ReactMarkdown from 'react-markdown'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Image } from '@telus/koodo-ui-kit'

// react-markdown gives each component no info about its ancestors, so nesting
// depth is tracked explicitly to style nested lists/items differently from top-level ones.
const ListDepthContext = createContext(0)

// Move component definitions outside of render
const createParagraphComponent = (paragraphClassForIndex) => {
    return ({ children, index }) => {
        if (paragraphClassForIndex) {
            const basePClass = paragraphClassForIndex(index)
            return <p className={basePClass}>{children}</p>
        }
        return <p className="text-copy">{children}</p>
    }
}

const createLinkComponent = (onLinkClick, linkPrimaryClass, linkFontClass) => ({ href, children }) => {
    const text = children[0]
    const handleClick = () => {
        if (text && onLinkClick) onLinkClick(text, href)
    }
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-id={text}
            onClick={handleClick}
            className={classNames(linkFontClass, "underline", { "text-black": linkPrimaryClass })}
        >
            {children}
        </a>
    )
}

const createListItemComponent = (listClass, listItemIcon, nestedListClass) => ({ children }) => {
    // depth > 1 (not > 0) because an li's own ul/ol ancestor already incremented
    // the context by the time the li reads it, so 1 is the top-level list's value.
    const depth = useContext(ListDepthContext)
    const isNested = depth > 1
    const baseListClass = isNested
        ? (nestedListClass || 'text-sm leading-5 font-body')
        : (listClass || 'text-sm leading-6 font-body')

    if (!listItemIcon || isNested) {
        return <li className={baseListClass}>{children}</li>
    }
    return (
        <li className={classNames('flex items-start mb-2', baseListClass)}>
            <div className="flex flex-row items-start">
                <Image src={listItemIcon?.url} alt={listItemIcon?.title} />
                <div className="pl-4">{children}</div>
            </div>
        </li>
    )
}

const createStrongComponent = (strongClass) => ({ children }) => (
    <strong className={strongClass || 'font-normal font-h1'}>{children}</strong>
)
const DelComponent = ({ children }) => <del>{children}</del>

const createUlComponent = (listItemIcon) => ({ children }) => {
    // depth > 0 here means this ul itself is rendered inside another list's li
    // (a genuinely nested list), as opposed to being the outermost list.
    const depth = useContext(ListDepthContext)
    const isNested = depth > 0
    const ulClassName = isNested
        ? "text-copy mt-1 mb-1 ml-6 list-disc"
        : classNames("text-copy mb-0 list-outside2", { "list-disc ml-4": !listItemIcon }, { "list-none": listItemIcon })

    return (
        <ListDepthContext.Provider value={depth + 1}>
            <ul className={ulClassName}>{children}</ul>
        </ListDepthContext.Provider>
    )
}

const createOlComponent = () => ({ children }) => {
    const depth = useContext(ListDepthContext)
    return (
        <ListDepthContext.Provider value={depth + 1}>
            <ol className="list-decimal text-copy">{children}</ol>
        </ListDepthContext.Provider>
    )
}

const MarkdownRenderer = ({
    markdown,
    onLinkClick,
    paragraphClassForIndex,
    listClass,
    listItemIcon,
    nestedListClass,
    linkPrimaryClass = true,
    linkFontClass = 'font-h1',
    strongClass,
    componentOverrides
}) => {
    const components = {
        p: createParagraphComponent(paragraphClassForIndex),
        a: createLinkComponent(onLinkClick, linkPrimaryClass, linkFontClass),
        strong: createStrongComponent(strongClass),
        del: DelComponent,
        ul: createUlComponent(listItemIcon),
        ol: createOlComponent(),
        li: createListItemComponent(listClass, listItemIcon, nestedListClass),
        ...componentOverrides
    }

    return (
        <ReactMarkdown components={components} includeElementIndex>
            {markdown || ''}
        </ReactMarkdown>
    )
}

MarkdownRenderer.propTypes = {
    markdown: PropTypes.string,
    onLinkClick: PropTypes.func,
    paragraphClassForIndex: PropTypes.func,
    listClass: PropTypes.string,
    nestedListClass: PropTypes.string,
    listItemIcon: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string
    }),
    linkPrimaryClass: PropTypes.bool,
    linkFontClass: PropTypes.string,
    strongClass: PropTypes.string,
    componentOverrides: PropTypes.objectOf(PropTypes.elementType)
}

export default MarkdownRenderer
